import { useApp } from '@/components/hooks/useApp';
import { useAccountingFirmContext } from '@/components/hooks/useAccountingFirmContext';

/**
 * useAdvisorSession — sesión del asesor en Modo Dios (programa PR-AD).
 *
 * **Path B (decisión de arquitectura).** El repo NO tiene un overlay sin estado
 * `acting_as_company_id`: el "Modo Dios" es el `switch-company` existente, que
 * setea `users.company_id` de sesión (y ya audita con
 * `performed_by_external_advisor`). Por eso `acting_as_company_id` se DERIVA de
 * la company de sesión (`useApp().company`), no de un header/overlay nuevo. El
 * aislamiento entre clientes de la asesoría recae en SEC-06 (`WHERE company_id`)
 * ya existente. Ver `backend/docs/advisor/PATH_B_MODEL.md` (PR-AD-1).
 *
 * El vocabulario del diseño (`acting_as_company_id`, `god_mode`) se conserva en
 * el shape de retorno para que los consumidores (`useClientSummary`,
 * `useAriaInsight`, …) lean siempre `acting_as_company_id` y lo metan en el
 * `queryKey` — que en la práctica es `company?.id`, cumpliendo la "regla de oro"
 * sin overlay.
 *
 * @typedef {Object} AdvisorSession
 * @property {string|null} firm_id              la asesoría (null si no es firm member)
 * @property {string|null} advisor_user_id      el usuario asesor
 * @property {string|null} acting_as_company_id cliente sobre el que opera (= company de sesión)
 * @property {boolean} god_mode                 true mientras opera como el cliente
 * @property {boolean} isLoading
 *
 * @returns {AdvisorSession}
 */
export function useAdvisorSession() {
  const { user, company, loading } = useApp();
  const { firm, isLoading: firmLoading } = useAccountingFirmContext();

  return {
    // `firm_id` solo aplica a asesores estructurales (fila en
    // accounting_firm_members). Para un owner/admin multi-empresa sin firm es null.
    firm_id: user?.firmMember ? (firm?.id ?? null) : null,
    advisor_user_id: user?.id ?? null,
    // Path B: la company de sesión ES el cliente sobre el que se opera.
    acting_as_company_id: company?.id ?? null,
    // El backend (getUserContext) ya resuelve effectiveRole por-tenant:
    // 'accountant_external' cuando el asesor entra a un cliente desde el portal.
    god_mode: user?.effectiveRole === 'accountant_external',
    isLoading: loading || firmLoading,
  };
}

export default useAdvisorSession;
